import {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Camera, Sun, UserRound} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {prefersReducedMotion} from '../../../utils/reducedMotion';
import {selfiePrepMp4, selfiePrepPoster, selfiePrepWebm} from './preloadSelfieGuide';
import {TimedCheckList} from './TimedCheckList';
import {useVideoCues} from './useVideoCues';

type Props = {
  className?: string;
};

const MEDIA_CLASS =
  'mb-2.5 mt-3.5 min-h-[280px] w-full flex-1 basis-0 object-contain mix-blend-multiply lg:min-h-[360px] lg:w-[min(100%,480px)]';

function SelfiePrepGuide({className}: Props) {
  const {t} = useTranslation('identity-verification');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isStill] = useState(prefersReducedMotion);
  const {currentTime, hasEnded} = useVideoCues(videoRef);

  const items = [
    {icon: <UserRound />, label: t('selfie_prep_remove_cover'), doneAt: 3.7},
    {icon: <Sun />, label: t('selfie_prep_light'), doneAt: 5.9},
    {icon: <Camera />, label: t('selfie_prep_take'), doneAt: 6.9},
  ];

  return (
    <div
      className={cn(
        'iv-selfie-prep flex min-h-0 w-full flex-1 flex-col items-center pb-6 lg:pb-0',
        className,
      )}
    >
      {isStill ? (
        <img
          src={selfiePrepPoster}
          alt={t('selfie_prep_video_label')}
          className={MEDIA_CLASS}
        />
      ) : (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          poster={selfiePrepPoster}
          aria-label={t('selfie_prep_video_label')}
          className={MEDIA_CLASS}
        >
          <source src={selfiePrepWebm} type="video/webm" />
          <source src={selfiePrepMp4} type="video/mp4" />
        </video>
      )}
      <TimedCheckList
        className="w-full"
        items={items}
        currentTime={currentTime}
        done={isStill || hasEnded}
      />
    </div>
  );
}

export {SelfiePrepGuide};
